import { Word } from '@axys-notes/common';

/**
 * Synchronise les mots d'un segment après modification du texte.
 *
 * Algorithme :
 * 1. Trouve les mots communs entre l'ancienne et la nouvelle version (LCS)
 * 2. Pour les mots communs, conserve leurs timestamps originaux
 * 3. Pour les nouveaux mots, interpole les timestamps entre le mot précédent
 *    et le prochain mot connu (de la LCS)
 *
 * @param oldWords - Liste originale des mots avec leurs timestamps
 * @param newText - Nouveau texte après édition
 * @param segmentStart - Timestamp de début du segment
 * @param segmentEnd - Timestamp de fin du segment
 * @returns Nouvelle liste de mots avec timestamps synchronisés
 */
export function syncWordsWithTimestamps(
  oldWords: Word[],
  newText: string,
  segmentStart: number,
  segmentEnd: number
): Word[] {
  // Extraire les nouveaux mots du texte
  const newWordStrings = newText.trim().split(/\s+/).filter(w => w.length > 0);

  if (newWordStrings.length === 0) {
    return [];
  }

  // Trouver les correspondances en utilisant LCS (Longest Common Subsequence)
  const lcs = findLCS(
    oldWords.map(w => w.word.toLowerCase()),
    newWordStrings.map(w => w.toLowerCase())
  );

  // Construire la nouvelle liste de mots
  const result: Word[] = [];
  let lcsPointer = 0;

  // Grouper les mots par "blocs" entre les mots de la LCS
  // Chaque bloc contient des nouveaux mots à interpoler
  let blockStartTime = segmentStart;
  let blockStartIndex = 0;

  for (let i = 0; i < newWordStrings.length; i++) {
    const newWord = newWordStrings[i];

    // Vérifier si ce mot fait partie de la LCS
    if (lcsPointer < lcs.length && lcs[lcsPointer].newIndex === i) {
      // Ce mot existe dans les anciens mots - réutiliser ses timestamps
      const oldWord = oldWords[lcs[lcsPointer].oldIndex];

      // D'abord, interpoler tous les mots en attente dans le bloc précédent
      if (blockStartIndex < i) {
        interpolateBlock(
          result,
          newWordStrings,
          blockStartIndex,
          i,
          blockStartTime,
          oldWord.start ?? segmentEnd
        );
      }

      // Ajouter le mot de la LCS
      result.push({
        word: newWord, // Garder la casse du nouveau texte
        start: oldWord.start,
        end: oldWord.end,
        score: oldWord.score,
      });

      // Mettre à jour le début du prochain bloc
      blockStartTime = oldWord.end ?? segmentEnd;
      blockStartIndex = i + 1;
      lcsPointer++;
    }
  }

  // Interpoler les mots restants après le dernier mot de la LCS
  if (blockStartIndex < newWordStrings.length) {
    interpolateBlock(
      result,
      newWordStrings,
      blockStartIndex,
      newWordStrings.length,
      blockStartTime,
      segmentEnd
    );
  }

  return result;
}

/**
 * Interpole les timestamps pour un bloc de nouveaux mots.
 * Répartit uniformément le temps disponible entre startTime et endTime.
 */
function interpolateBlock(
  result: Word[],
  allNewWords: string[],
  startIndex: number,
  endIndex: number,
  startTime: number,
  endTime: number
): void {
  const wordCount = endIndex - startIndex;
  if (wordCount <= 0) return;

  const totalTime = endTime - startTime;
  const timePerWord = totalTime / wordCount;

  for (let i = startIndex; i < endIndex; i++) {
    const offset = i - startIndex;
    result.push({
      word: allNewWords[i],
      start: startTime + offset * timePerWord,
      end: startTime + (offset + 1) * timePerWord,
      score: 1, // Confiance maximale car c'est une correction manuelle
    });
  }
}

/**
 * Trouve la plus longue sous-séquence commune (LCS) entre deux listes de mots.
 * Retourne les indices correspondants dans les deux listes.
 */
function findLCS(
  oldWords: string[],
  newWords: string[]
): Array<{ oldIndex: number; newIndex: number }> {
  const m = oldWords.length;
  const n = newWords.length;

  // Construire la matrice LCS
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldWords[i - 1] === newWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Reconstruire la LCS en remontant la matrice
  const result: Array<{ oldIndex: number; newIndex: number }> = [];
  let i = m;
  let j = n;

  while (i > 0 && j > 0) {
    if (oldWords[i - 1] === newWords[j - 1]) {
      result.unshift({ oldIndex: i - 1, newIndex: j - 1 });
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  return result;
}
